import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Bell, Wifi, Shield, Tag, User, Zap } from 'lucide-react';

const ONESIGNAL_APP_ID = '716aa58f-2072-4a62-b2af-99bd34dc237e';

function StatusIcon({ status }) {
    if (status === 'ok') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (status === 'error') return <XCircle className="w-4 h-4 text-red-500" />;
    return <AlertTriangle className="w-4 h-4 text-amber-500" />;
}

function DiagRow({ icon: Icon, label, value, status }) {
    return (
        <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
            <Icon className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <span className="text-sm text-slate-600 flex-1 min-w-0">{label}</span>
            <div className="flex items-center gap-2 shrink-0">
                <code className="text-xs bg-slate-50 px-2 py-0.5 rounded max-w-[250px] truncate block">{String(value)}</code>
                <StatusIcon status={status} />
            </div>
        </div>
    );
}

export default function DiagnosticPage() {
    const { user } = useAuth();
    const [diag, setDiag] = useState(null);
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);

    const addLog = (msg, ok = null) => {
        setLogs(prev => [...prev, { msg, ok, time: new Date().toLocaleTimeString() }]);
    };

    const runDiagnostic = useCallback(async () => {
        setLoading(true);
        const result = {};

        // Browser support
        result.browser_notifications = 'Notification' in window;
        result.service_worker = 'serviceWorker' in navigator;
        result.notification_permission = 'Notification' in window ? Notification.permission : 'N/A';

        // Service Workers
        try {
            const regs = await navigator.serviceWorker.getRegistrations();
            result.sw_count = regs.length;
            result.sw_scopes = regs.map(r => r.scope).join(', ') || 'aucun';
            // Check if OneSignal SW is registered
            result.has_onesignal_sw = regs.some(r => r.active?.scriptURL?.includes('OneSignal'));
        } catch (e) {
            result.sw_count = 0;
            result.sw_scopes = 'Erreur: ' + e.message;
            result.has_onesignal_sw = false;
        }

        // Direct Push API check (bypasses OneSignal)
        try {
            const swReg = await navigator.serviceWorker.getRegistration('/');
            if (swReg) {
                const sub = await swReg.pushManager.getSubscription();
                result.direct_push_sub = sub ? sub.endpoint.substring(0, 60) + '...' : 'AUCUN';
                result.has_push_sub = !!sub;
            } else {
                result.direct_push_sub = 'Pas de SW enregistré';
                result.has_push_sub = false;
            }
        } catch (e) {
            result.direct_push_sub = 'Erreur: ' + e.message;
            result.has_push_sub = false;
        }

        // OneSignal state
        result.onesignal_loading = !!window.__onesignal_loading;
        result.onesignal_ready = !!window.__onesignal_ready;
        result.onesignal_error = window.__onesignal_error || null;

        const OS = window.OneSignal;
        result.onesignal_exists = !!OS;

        if (OS) {
            try {
                result.push_supported = OS.Notifications ? OS.Notifications.isPushSupported() : false;
                result.os_permission = OS.Notifications ? OS.Notifications.permission : 'N/A';
                result.opted_in = OS.User?.PushSubscription?.optedIn ?? 'N/A';
                result.subscription_id = OS.User?.PushSubscription?.id ?? 'AUCUN';
                result.push_token = OS.User?.PushSubscription?.token
                    ? OS.User.PushSubscription.token.substring(0, 50) + '...'
                    : 'AUCUN';
            } catch (e) {
                result.os_error = e.message;
            }
        }

        result.user_id = user?.id || 'Non connecté';
        result.user_role = user?.role || 'N/A';
        result.url = window.location.origin;

        setDiag(result);
        setLoading(false);
    }, [user]);

    useEffect(() => { runDiagnostic(); }, [runDiagnostic]);

    // Manual push subscription creation (bypasses OneSignal SDK entirely)
    const createManualPushSubscription = async () => {
        setLogs([]);
        try {
            // Step 1: Request permission
            addLog('Demande de permission navigateur...');
            if (!('Notification' in window)) {
                addLog('API Notification non disponible dans ce navigateur', false);
                return;
            }
            let perm = Notification.permission;
            if (perm === 'default') {
                perm = await Notification.requestPermission();
            }
            if (perm !== 'granted') {
                addLog(`Permission refusée: ${perm}`, false);
                return;
            }
            addLog(`Permission: ${perm}`, true);

            // Step 2: Register service worker
            addLog('Enregistrement du Service Worker...');
            let swReg;
            try {
                swReg = await navigator.serviceWorker.getRegistration('/');
                if (!swReg) {
                    swReg = await navigator.serviceWorker.register('/OneSignalSDKWorker.js', { scope: '/' });
                    addLog('SW enregistré (nouveau)', true);
                } else {
                    addLog(`SW déjà actif: ${swReg.active?.scriptURL || swReg.scope}`, true);
                }
                await navigator.serviceWorker.ready;
                addLog('SW prêt', true);
            } catch (swErr) {
                addLog(`Erreur SW: ${swErr.message}`, false);
                return;
            }

            // Step 3: Get VAPID key from OneSignal config
            addLog('Récupération de la clé VAPID...');
            let vapidKey;
            try {
                const syncResp = await fetch(`https://api.onesignal.com/sync/${ONESIGNAL_APP_ID}/web`);
                const syncData = await syncResp.json();
                vapidKey = syncData?.config?.vapid_public_key;
                if (!vapidKey) throw new Error('Clé VAPID non trouvée dans la config');
                addLog(`Clé VAPID: ${vapidKey.substring(0, 20)}...`, true);
            } catch (e) {
                addLog(`Erreur VAPID: ${e.message}`, false);
                return;
            }

            // Step 4: Create push subscription
            addLog('Création de l\'abonnement push...');
            try {
                // Unsubscribe first if exists
                const existingSub = await swReg.pushManager.getSubscription();
                if (existingSub) {
                    await existingSub.unsubscribe();
                    addLog('Ancien abonnement supprimé', true);
                }

                // Convert VAPID key
                const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
                const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
                const rawData = window.atob(base64);
                const outputArray = new Uint8Array(rawData.length);
                for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

                const subscription = await swReg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: outputArray,
                });

                const endpoint = subscription.endpoint;
                addLog(`Abonnement créé! Endpoint: ${endpoint.substring(0, 60)}...`, true);

                // Step 5: Register with OneSignal
                addLog('Enregistrement sur OneSignal...');
                const subJson = subscription.toJSON();
                const playerData = {
                    app_id: ONESIGNAL_APP_ID,
                    device_type: 5,
                    identifier: endpoint,
                    web_auth: subJson.keys?.auth || '',
                    web_p256: subJson.keys?.p256dh || '',
                    tags: {},
                };

                // Add user ID tag
                if (user?.id) {
                    playerData.tags.user_id = user.id;
                    playerData.tags.role = user.role || 'client';
                }

                const resp = await fetch('https://api.onesignal.com/players', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(playerData),
                });
                const result = await resp.json();

                if (result.success || result.id) {
                    addLog(`Player créé sur OneSignal! ID: ${result.id}`, true);

                    // Also try OneSignal SDK login + tags
                    const OS = window.OneSignal;
                    if (OS && window.__onesignal_ready && user?.id) {
                        try {
                            await OS.login(user.id);
                            addLog('OneSignal login OK', true);
                        } catch (e) {
                            addLog(`OneSignal login: ${e.message}`, false);
                        }
                    }

                    addLog('SUCCÈS! Vous devriez maintenant recevoir les notifications push.', true);
                } else {
                    addLog(`Erreur OneSignal: ${JSON.stringify(result)}`, false);
                }
            } catch (pushErr) {
                addLog(`Erreur push subscribe: ${pushErr.message}`, false);
            }

            // Refresh diagnostics
            setTimeout(runDiagnostic, 2000);

        } catch (e) {
            addLog(`Erreur générale: ${e.message}`, false);
        }
    };

    // OneSignal SDK optIn attempt
    const tryOneSignalOptIn = async () => {
        setLogs([]);
        const OS = window.OneSignal;
        if (!OS || !window.__onesignal_ready) {
            addLog('OneSignal SDK non prêt', false);
            return;
        }
        try {
            addLog('Tentative optIn via OneSignal SDK...');
            if ('Notification' in window && Notification.permission === 'default') {
                addLog('Demande de permission...');
                await Notification.requestPermission();
            }
            addLog(`Permission: ${Notification.permission}`);
            await OS.User.PushSubscription.optIn();
            await new Promise(r => setTimeout(r, 2000));
            const optedIn = OS.User.PushSubscription.optedIn;
            const token = OS.User.PushSubscription.token;
            addLog(`OptedIn: ${optedIn}, Token: ${token ? token.substring(0, 40) + '...' : 'AUCUN'}`, optedIn);
            if (user?.id) {
                await OS.login(user.id);
                addLog('Login OK', true);
            }
        } catch (e) {
            addLog(`Erreur: ${e.message}`, false);
        }
        setTimeout(runDiagnostic, 1000);
    };

    const clearAndReload = async () => {
        setLogs([]);
        addLog('Nettoyage des Service Workers...');
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) await reg.unregister();
        addLog(`${regs.length} SW supprimés`);
        const keys = await caches.keys();
        for (const key of keys) await caches.delete(key);
        addLog(`${keys.length} caches supprimés`);
        addLog('Rechargement dans 2s...');
        setTimeout(() => window.location.reload(), 2000);
    };

    return (
        <div className="min-h-screen bg-slate-50 p-4 md:p-8">
            <div className="max-w-2xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-slate-900">Diagnostic Push</h1>
                    <Button variant="outline" size="sm" onClick={runDiagnostic} disabled={loading} data-testid="refresh-diag">
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
                    </Button>
                </div>

                {user && (
                    <Card className="border-slate-200 rounded-xl">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-600">{user.email} ({user.role})</span>
                                <code className="text-[10px] bg-slate-100 px-1 rounded ml-auto">{user.id}</code>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {diag && (
                    <>
                        <Card className="border-slate-200 rounded-xl">
                            <CardHeader className="pb-0 pt-4 px-4">
                                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Wifi className="w-4 h-4" /> Navigateur & Service Worker</span>
                            </CardHeader>
                            <CardContent className="px-4 pb-3">
                                <DiagRow icon={Bell} label="API Notifications" value={String(diag.browser_notifications)} status={diag.browser_notifications ? 'ok' : 'error'} />
                                <DiagRow icon={Shield} label="API Service Worker" value={String(diag.service_worker)} status={diag.service_worker ? 'ok' : 'error'} />
                                <DiagRow icon={Bell} label="Permission" value={diag.notification_permission} status={diag.notification_permission === 'granted' ? 'ok' : diag.notification_permission === 'denied' ? 'error' : 'warn'} />
                                <DiagRow icon={Shield} label="Service Workers" value={`${diag.sw_count} (${diag.sw_scopes})`} status={diag.sw_count > 0 ? 'ok' : 'warn'} />
                                <DiagRow icon={Shield} label="OneSignal SW" value={String(diag.has_onesignal_sw)} status={diag.has_onesignal_sw ? 'ok' : 'error'} />
                                <DiagRow icon={Zap} label="Abonnement Push (direct)" value={diag.direct_push_sub} status={diag.has_push_sub ? 'ok' : 'error'} />
                            </CardContent>
                        </Card>

                        <Card className="border-slate-200 rounded-xl">
                            <CardHeader className="pb-0 pt-4 px-4">
                                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Bell className="w-4 h-4" /> OneSignal SDK</span>
                            </CardHeader>
                            <CardContent className="px-4 pb-3">
                                <DiagRow icon={Wifi} label="SDK chargé" value={String(diag.onesignal_exists)} status={diag.onesignal_exists ? 'ok' : 'error'} />
                                <DiagRow icon={Wifi} label="SDK prêt (ready)" value={String(diag.onesignal_ready)} status={diag.onesignal_ready ? 'ok' : 'error'} />
                                {diag.onesignal_error && <DiagRow icon={AlertTriangle} label="Erreur init" value={diag.onesignal_error} status="error" />}
                                <DiagRow icon={CheckCircle} label="Opted-in" value={String(diag.opted_in)} status={diag.opted_in === true ? 'ok' : 'error'} />
                                <DiagRow icon={Tag} label="Subscription ID" value={String(diag.subscription_id)} status={diag.subscription_id !== 'AUCUN' ? 'ok' : 'error'} />
                                <DiagRow icon={Tag} label="Push Token" value={String(diag.push_token)} status={diag.push_token !== 'AUCUN' ? 'ok' : 'error'} />
                            </CardContent>
                        </Card>
                    </>
                )}

                <Card className="border-emerald-200 rounded-xl bg-emerald-50/30">
                    <CardContent className="p-4 space-y-3">
                        <p className="text-sm font-semibold text-slate-700">Actions</p>
                        <div className="flex flex-wrap gap-2">
                            <Button onClick={createManualPushSubscription} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="manual-push-btn">
                                <Zap className="w-4 h-4 mr-1.5" /> Créer abonnement push (Manuel)
                            </Button>
                            <Button onClick={tryOneSignalOptIn} variant="outline" data-testid="onesignal-optin-btn">
                                <Bell className="w-4 h-4 mr-1.5" /> OneSignal OptIn
                            </Button>
                            <Button onClick={clearAndReload} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="clear-sw-btn">
                                <RefreshCw className="w-4 h-4 mr-1.5" /> Nettoyer & Recharger
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {logs.length > 0 && (
                    <Card className="border-slate-200 rounded-xl">
                        <CardHeader className="pb-0 pt-4 px-4">
                            <span className="text-sm font-semibold text-slate-700">Journal</span>
                        </CardHeader>
                        <CardContent className="px-4 pb-3">
                            <div className="space-y-1 max-h-80 overflow-y-auto">
                                {logs.map((log, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs py-1 border-b border-slate-50">
                                        <span className="text-slate-300 shrink-0">{log.time}</span>
                                        {log.ok === true && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />}
                                        {log.ok === false && <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />}
                                        {log.ok === null && <span className="w-3.5 shrink-0" />}
                                        <span className={`${log.ok === false ? 'text-red-600' : log.ok === true ? 'text-emerald-700' : 'text-slate-600'} break-all`}>{log.msg}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <p className="text-xs text-slate-400 text-center">
                    URL: {diag?.url} — Prenez une capture d'écran de cette page et envoyez-la.
                </p>
            </div>
        </div>
    );
}
